import { useState, Fragment } from 'react'
import { useRenewal } from '../../../context/renewal'
import { PolicyTypeOptions } from 'src/dtos/applicationData'
import { QuoteDataWithId } from 'src/dtos/custom'
import {
  ApplicationBodyContainer, 
  ApplicationBodyContent,
  DefaultTitle, 
  DefaultSubtitle,
  FormContainer,
  PolicyTable,
  PolicyTableContainer,
  PolicyTypeContainer,
  HelperButton
} from '../../../components/application/defaultComponents'
import { FlexDiv } from 'src/components/application/formComponents/styles'
import PrebuiltRenewalFooter from '../../../components/application/PrebuiltRenewalFooter'
import QuoteDetailBox from 'src/components/application/formComponents/QuoteDetailBox'
import Button from 'src/components/basic/Button'
import produce from 'immer'
import { generateRandomNumberId } from 'src/utils/math'
import { isClaimsTriggered } from 'src/utils/renewals'
import { tlieSources } from 'src/constants/tlie'
import { FirmQuoteSchema } from 'src/yup/RenewalSchema'

const Select = () => {
  const quoteBoxErrorCodes = [ 'EC121', 'EC122']
  const { renewalData } = useRenewal()
  const [showQuoteSelection, setShowQuoteSelection] = useState(false)
  const [quotes, setQuotes] = useState<QuoteDataWithId[]>(() => {
    return renewalData?.quotesSection?.quotes && renewalData?.quotesSection?.quotes.length > 0 ?
    renewalData.quotesSection.quotes.map(quote => ({ ...quote, id: generateRandomNumberId() })) :
      []
  })
  const [showErrors, setShowErrors] = useState(false)

  const handleSetQuote = (index: number, quote: QuoteDataWithId) => {
    setQuotes(produce(draft => {
      draft[index] = quote
    }))
  }

  const handleSelectQuote = (policyType: PolicyTypeOptions) => {
    setQuotes(prev => [...prev, { policyType, id: generateRandomNumberId() }])
    setShowQuoteSelection(false)
  }

  const handleRemoveQuote = (index: number) => {
    setQuotes(prev => prev.filter((_, idx) => idx !== index))
  }

  const validatePage = () => {
    let validations = quotes.map(att => {
      try {
        FirmQuoteSchema.validateSync(att, { abortEarly: false })
        return true
      } catch(err) {
        return false
      }
    })

    for (let index = 0; index < quotes.length; index++) {
      const testQuotes = quotes.filter((qt, idx) => idx !== index);

      const duplicate = testQuotes.find(
        qt => 
          qt.deductible === quotes[index].deductible &&
          qt.liabilityLimits === quotes[index].liabilityLimits &&
          qt.policyType === quotes[index].policyType
      )

      if(!!duplicate) {
        validations.push(false)
        break;
      }
    }
    
    return validations
  }

  const updatedApplicationData = () => ({ 
    ...renewalData,
    currentPage: window.location.pathname,
    quotesSection: {
      quotes: quotes.map(quote => {
        const { id, ...rest } = quote
        return rest
      })
    }
  })

  const handleScrollIfInvalid = () => {
    const validations = validatePage()
    if(validations.some(vali => vali === false)) {
      setShowErrors(true)
      const firstInvalidIndex = validations.findIndex(inv => inv === false)
      document.getElementById(`qt__${firstInvalidIndex}`)?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const goBackRoute = () => {
    if(isClaimsTriggered(updatedApplicationData()) === true) {
      return '/customer/renew/questions/claims'
    }
    
    return '/customer/renew/questions/main'
  }
  
  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='94%'>
        <DefaultTitle>Confirm the policy you’d like to renew.</DefaultTitle>
        <DefaultSubtitle>
        You can edit your existing policy or add additional quotes for your renewal application. <HelperButton onClick={() => {}}><a target="_blank" rel="noreferrer noopener" href={tlieSources.policiesUrl}>Compare policies</a></HelperButton>
        </DefaultSubtitle>
        <FormContainer maxWidth='100%'>
          {quotes.map((quote, index) => (
            <Fragment key={quote.id}>
              {index > 0 ? (
                <DefaultSubtitle style={{ fontSize: '16px', margin: '64px 0 -16px' }}>
                  SELECT YOUR POLICY’S LIMITS & DEDUCTIBLE
                </DefaultSubtitle>
              ) : null}
              <div id={'qt__'+index}>
              </div>
              <QuoteDetailBox
                quote={quote}
                setQuote={quote => handleSetQuote(index, quote)}
                showErrors={showErrors}
                errorCodes={quoteBoxErrorCodes.map(e => `${e}:${index}`)}
              />
              {index > 0 ? (
                <FlexDiv maxWidth='320px' height='fit-content'>
                  <Button
                    variant='error'
                    onClick={() => handleRemoveQuote(index)}
                    style={{width: '100%'}}
                  >
                    Remove this quote
                  </Button>
                </FlexDiv>
              ) : null}
            </Fragment>
          ))}
        </FormContainer>
        {showQuoteSelection ? (
          <FlexDiv height='fit-content' maxWidth='600px' margin='64px 0 0 0'>
            <PolicyTableContainer>
              <h6>SELECT POLICY TYPE</h6>
              <PolicyTable>
                <tbody>
                  <tr>
                    <td>
                      <PolicyTypeContainer>
                        <h5>Premier Policy</h5>
                        <p>This is our top policy offering. It has the most service offerings; including these things.</p>
                        <Button onClick={() => handleSelectQuote('REGULAR')}>Start Quote</Button>
                      </PolicyTypeContainer>
                    </td>
                    <td>
                      <PolicyTypeContainer>
                        <h5>Essential Policy</h5>
                        <p>This is our standard policy. It has some limitations both on services and limits of liability.</p>
                        <Button onClick={() => handleSelectQuote('BASIC')}>Start Quote</Button>
                      </PolicyTypeContainer>
                    </td>
                  </tr>
                </tbody>
              </PolicyTable>
            </PolicyTableContainer>
          </FlexDiv>
        ) : null}
        <FlexDiv margin='64px 0 0 0' maxWidth='320px' height='fit-content'>
          <Button
            variant="secondary"
            onClick={() => setShowQuoteSelection(true)}
            disabled={showQuoteSelection}
            style={{width: '100%'}}
          >
            Add new quote
          </Button>
        </FlexDiv>
      </ApplicationBodyContent>
      <PrebuiltRenewalFooter
        goBackRoute={goBackRoute()}
        isFormValid={validatePage().every(att => att === true)}
        nextRoute="/customer/renew/review/firm"
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollIfInvalid}
      />
    </ApplicationBodyContainer>
  )
}

export default Select